// EN-1734: Super SIM data usage per AGM for the Device Info panel. Fetched on
// selection, at most every SIM_DATA_USAGE_MIN_REFETCH_MS per AGM, one request
// per AGM in flight. Failures are logged quietly and never notify; they are
// recorded in failedByUid so the panel shows "Data usage unavailable".
import { ISimDataUsage } from '../../models/SimDataUsage';
import { isSimDataUsageFresh } from '../../utils/SimDataUsageDisplay';

export interface SimDataUsageState {
  byUid: Record<number, ISimDataUsage>;
  /** AGMs whose last data-usage call failed (network, 5xx, bad body). */
  failedByUid: Record<number, boolean>;
}

export interface SimDataUsageApi {
  getSimDataUsage: (uid: number) => Promise<{ data: ISimDataUsage }>;
}

let api: SimDataUsageApi | null = null;
let now: () => number = () => Date.now();
const lastFetchedAt = new Map<number, number>();
const inFlight = new Map<number, Promise<ISimDataUsage | null>>();
/** Bumped on reset (sign-out) so answers still in flight are dropped. */
let generation = 0;

/** Replaces the WebApi call (unit specs); null restores DeviceService. */
export function setSimDataUsageApi(value: SimDataUsageApi | null): void {
  api = value;
}

/** Replaces the clock (unit specs); null restores Date.now. */
export function setSimDataUsageClock(value: (() => number) | null): void {
  now = value || (() => Date.now());
}

/**
 * Forgets fetch times and in-flight requests (unit specs, sign-out); answers
 * still in flight are not stored.
 */
export function resetSimDataUsageThrottle(): void {
  lastFetchedAt.clear();
  inFlight.clear();
  generation++;
}

async function getApi(): Promise<SimDataUsageApi> {
  if (!api) {
    // Lazy: DeviceService imports the root store.
    api = (await import('../../services/DeviceService')).default;
  }
  return api;
}

function isUsage(value: any, uid: number): value is ISimDataUsage {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof value.hasSim === 'boolean' &&
    typeof value.state === 'string' &&
    (value.uid === undefined || value.uid === uid)
  );
}

export default {
  state: (): SimDataUsageState => ({
    byUid: {},
    failedByUid: {},
  }),
  mutations: {
    SET_SIM_DATA_USAGE(state: SimDataUsageState, usage: ISimDataUsage) {
      state.byUid = { ...state.byUid, [usage.uid]: usage };
      if (state.failedByUid[usage.uid]) {
        const failed = { ...state.failedByUid };
        delete failed[usage.uid];
        state.failedByUid = failed;
      }
    },
    SET_SIM_DATA_USAGE_FAILED(state: SimDataUsageState, uid: number) {
      state.failedByUid = { ...state.failedByUid, [uid]: true };
    },
    /** Sign-out / another user: drop every AGM's usage. */
    CLEAR_SIM_DATA_USAGE(state: SimDataUsageState) {
      state.byUid = {};
      state.failedByUid = {};
    },
  },
  actions: {
    /**
     * Loads usage for `uid` unless it was fetched within the refetch window
     * (`force` skips the window). Resolves to the usage, or null when skipped
     * or failed; never rejects.
     */
    fetchSimDataUsage(
      context: any,
      payload: number | { uid: number; force?: boolean }
    ): Promise<ISimDataUsage | null> {
      const uid = typeof payload === 'number' ? payload : payload?.uid;
      const force = typeof payload === 'object' && !!payload?.force;
      if (!uid || uid <= 0) return Promise.resolve(null);

      const pending = inFlight.get(uid);
      if (pending) return pending;
      if (!force && isSimDataUsageFresh(lastFetchedAt.get(uid), now())) {
        return Promise.resolve(context.state.byUid[uid] ?? null);
      }

      lastFetchedAt.set(uid, now()); // failures wait out the window too
      const startedIn = generation;
      const request = (async () => {
        try {
          const response = await (await getApi()).getSimDataUsage(uid);
          if (startedIn !== generation) return null; // signed out meanwhile
          const data = response?.data;
          if (!isUsage(data, uid)) {
            context.commit('SET_SIM_DATA_USAGE_FAILED', uid);
            return null;
          }
          const usage = { ...data, uid };
          context.commit('SET_SIM_DATA_USAGE', usage);
          return usage;
        } catch (error: any) {
          if (startedIn !== generation) return null;
          // Quiet by design: no notify; the panel shows "unavailable".
          context.commit('SET_SIM_DATA_USAGE_FAILED', uid);
          console.debug(
            `EN-1734: data usage for AGM ${uid} unavailable`,
            error?.response?.status ?? error?.message ?? error
          );
          return null;
        } finally {
          if (startedIn === generation) inFlight.delete(uid);
        }
      })();
      inFlight.set(uid, request);
      return request;
    },
  },
  getters: {},
};
